/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';

import {
    IllustratedMessage,
    Heading,
    Content,
    Image,
    Text,
    View,
    Cell,
    Column,
    Row,
    TableView,
    TableBody,
    TableHeader
} from '@adobe/react-spectrum';
import { ActionMenu, Item as ActionMenuItem } from '@react-spectrum/menu';
import OpenIn from '@spectrum-icons/workflow/OpenIn';
import ViewDetail from '@spectrum-icons/workflow/ViewDetail';
import FolderOpen from '@spectrum-icons/workflow/FolderOpen';
import NotFound from '@spectrum-icons/illustrations/NotFound';

import { useNavigate } from '../common/useNavigate';
import useReferenceData from './useReferenceData';
import { dam_console, dam_details, properties_dam } from './urls';

import classes from './ReferenceTable.scss';

const messages = defineMessages({
    errorNoResultsFound: {
        id: 'error.noResultsFound',
        defaultMessage: 'No results found.'
    },
    errorNoAssetsAssigned: {
        id: 'error.noAssetsAssigned',
        defaultMessage: 'No assets assigned.'
    },
    actionOpenFolder: {
        id: 'action.openFolder',
        defaultMessage: 'Open Folder'
    },
    actionOpenAsset: {
        id: 'action.openAsset',
        defaultMessage: 'Open Asset'
    },
    actionOpenProperties: {
        id: 'action.openProperties',
        defaultMessage: 'Open Properties'
    },
    labelAssociatedAssetTable: {
        id: 'label.associatedAssetTable',
        defaultMessage: 'Table of associated assets'
    },
    columnAsset: {
        id: 'column.asset',
        defaultMessage: 'Asset'
    },
    columnName: {
        id: 'column.name',
        defaultMessage: 'Name'
    },
    columnPath: {
        id: 'column.path',
        defaultMessage: 'Path'
    },
    columnLastUpdated: {
        id: 'column.lastUpdated',
        defaultMessage: 'Last Updated'
    },
    columnActions: {
        id: 'column.actions',
        defaultMessage: 'Actions'
    }
});

/**
 * Displays a TableView that shows assets associated to products or categories. The data is fetched from the AdminDataServlet.
 */
const AssetReferenceTable = props => {
    const { type, identifier } = props;
    const { formatMessage, formatDate, formatTime } = useIntl();
    const { open } = useNavigate();

    const { items, loadingState, loadMore } = useReferenceData({ type, identifier, operation: 'damReference' });

    const renderEmptyState = () => {
        return (
            <IllustratedMessage>
                <NotFound />
                <Heading>{formatMessage(messages.errorNoResultsFound)}</Heading>
                <Content>{formatMessage(messages.errorNoAssetsAssigned)}</Content>
            </IllustratedMessage>
        );
    };

    const renderActionMenu = item => (
        <ActionMenu
            isQuiet
            onAction={key => {
                switch (key) {
                    case 'folder': {
                        const folderPath = item.path.split('/').slice(0, -1).join('/');
                        open(dam_console(folderPath));
                        return;
                    }
                    case 'open':
                        open(dam_details(item.path));
                        return;
                    case 'properties':
                        open(properties_dam(item.path));
                        return;
                }
            }}
        >
            <ActionMenuItem key="folder" textValue={formatMessage(messages.actionOpenFolder)}>
                <FolderOpen size="S" />
                <Text>{formatMessage(messages.actionOpenFolder)}</Text>
            </ActionMenuItem>
            <ActionMenuItem key="open" textValue={formatMessage(messages.actionOpenAsset)}>
                <OpenIn size="S" />
                <Text>{formatMessage(messages.actionOpenAsset)}</Text>
            </ActionMenuItem>
            <ActionMenuItem key="properties" textValue={formatMessage(messages.actionOpenProperties)}>
                <ViewDetail size="S" />
                <Text>{formatMessage(messages.actionOpenProperties)}</Text>
            </ActionMenuItem>
        </ActionMenu>
    );

    return (
        <View UNSAFE_className={classes.root}>
            <TableView
                overflowMode="wrap"
                aria-label={formatMessage(messages.labelAssociatedAssetTable)}
                minHeight="size-3000"
                isQuiet
                renderEmptyState={renderEmptyState}
            >
                <TableHeader>
                    <Column key="thumbnail" width={75} hideHeader align="center">
                        {formatMessage(messages.columnAsset)}
                    </Column>
                    <Column isRowHeader key="title">
                        {formatMessage(messages.columnName)}
                    </Column>
                    <Column key="path">{formatMessage(messages.columnPath)}</Column>
                    <Column key="lastModified">{formatMessage(messages.columnLastUpdated)}</Column>
                    <Column key="actions" hideHeader align="center" width={50}>
                        {formatMessage(messages.columnActions)}
                    </Column>
                </TableHeader>
                <TableBody items={items} loadingState={loadingState} onLoadMore={loadMore}>
                    {item => (
                        <Row key={item.path}>
                            {columnKey => {
                                switch (columnKey) {
                                    case 'thumbnail':
                                        return (
                                            <Cell>
                                                <View padding="size-75">
                                                    <Image objectFit="contain" src={item.thumbnail} alt={item.title} />
                                                </View>
                                            </Cell>
                                        );
                                    case 'title':
                                        return (
                                            <Cell>
                                                <span className={classes.title}>{item.name}</span>
                                                <span className={classes.subTitle}>{item.title}</span>
                                            </Cell>
                                        );
                                    case 'lastModified': {
                                        if (isNaN(item.lastModified)) {
                                            return <Cell />;
                                        }
                                        const date = `${formatDate(item.lastModified)} - ${formatTime(
                                            item.lastModified,
                                            { timeStyle: 'medium' }
                                        )}`;
                                        if (item.lastModifiedBy) {
                                            return (
                                                <Cell>
                                                    <span className={classes.title}>{date}</span>
                                                    <span className={classes.subTitle}>{item.lastModifiedBy}</span>
                                                </Cell>
                                            );
                                        }
                                        return <Cell>{date}</Cell>;
                                    }
                                    case 'actions':
                                        return <Cell>{renderActionMenu(item)}</Cell>;
                                    default:
                                        return <Cell>{item[columnKey]}</Cell>;
                                }
                            }}
                        </Row>
                    )}
                </TableBody>
            </TableView>
        </View>
    );
};

AssetReferenceTable.propTypes = {
    type: PropTypes.oneOf(['product', 'category']),
    identifier: PropTypes.string
};

export default AssetReferenceTable;
